import Button from "@mui/material/Button";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import { ProgressCell } from "../domains";
import { formatDuration } from "./mappers";
import type { QueueJobRow } from "./types";

interface ColumnsParams {
  retryJob: (id: string) => void;
  promoteJob: (id: string) => void;
}

const renderDate = (value: unknown) => {
  const date = value as string | null;
  return date ? <CustomCellDate date={date} /> : null;
};

export const getColumnsConfig = ({
  retryJob,
  promoteJob,
}: ColumnsParams): ColumnDefinition<QueueJobRow>[] => [
  {
    name: "id",
    label: "ID",
    options: {
      sort: false,
      customBodyRender: (value: unknown) => (
        <CrudLink tab="details" id={value as string}>
          {value as string}
        </CrudLink>
      ),
    },
  },
  { name: "name", label: "Название", options: { sort: false } },
  {
    name: "progress",
    label: "Прогресс",
    options: {
      sort: false,
      setCellHeaderProps: () => ({ style: { minWidth: 160 } }),
      customBodyRender: (value: unknown) => {
        const [steps, progress, state] = value as [
          (string | null)[] | null,
          number | null,
          string | null,
        ];
        return <ProgressCell steps={steps} progress={progress} state={state} />;
      },
    },
  },
  { name: "state", label: "Статус", options: { sort: false } },
  { name: "attemptsMade", label: "Попыток", options: { sort: false } },
  { name: "failedReason", label: "Причина ошибки", options: { sort: false } },
  {
    name: "duration",
    label: "Длительность",
    options: {
      sort: false,
      customBodyRender: (value: unknown) => {
        const [finishedOn, processedOn] = value as [
          string | null,
          string | null,
        ];
        return formatDuration(finishedOn, processedOn);
      },
    },
  },
  {
    name: "waiting",
    label: "Ожидание",
    options: {
      sort: false,
      // В референсе тут считается finishedOn − timestamp, то есть полное время
      // жизни задачи, а не ожидание; берём processedOn − timestamp — время от
      // постановки в очередь до момента, когда задачу взял воркер
      customBodyRender: (value: unknown) => {
        const [processedOn, timestamp] = value as [
          string | null,
          string | null,
        ];
        return formatDuration(processedOn, timestamp);
      },
    },
  },
  {
    name: "timestamp",
    label: "Создана",
    options: { sort: false, customBodyRender: renderDate },
  },
  {
    name: "finishedOn",
    label: "Завершена",
    options: { sort: false, customBodyRender: renderDate },
  },
  {
    name: "processedOn",
    label: "Взята в работу",
    options: { sort: false, customBodyRender: renderDate },
  },
  {
    name: "actions",
    label: "Действия",
    options: {
      sort: false,
      customBodyRender: (value: unknown) => {
        const [id, state] = value as [string, string | null];

        if (state === "failed") {
          return (
            <Button size="small" onClick={() => retryJob(id)}>
              повторить
            </Button>
          );
        }

        if (state === "delayed") {
          return (
            <Button size="small" onClick={() => promoteJob(id)}>
              ускорить
            </Button>
          );
        }

        return null;
      },
    },
  },
];

export const getTextsConfig = (queueName: string, status: string) => ({
  listTitle: `Задачи ${queueName} · ${status}`,
  refetchList: "Обновить список",
  deleteItems: "Удалить задачи",
  itemsDeleted: "Задачи удалены",
  cancel: "Отмена",
  editItem: "Задача очереди",
  showTitle: "Детали задачи",
});
